import { nanoid } from 'nanoid'
import { getTabMetadata } from './metadata'
import {
  Tab,
  TAB_TYPE_CHAT,
  TAB_TYPE_DATA_GOVERNANCE_DASHBOARD,
  TAB_TYPE_DATABASE_METADATA,
  TAB_TYPE_DATABASE_SCHEMA,
  TAB_TYPE_DATABASE_TABLE,
  TAB_TYPE_TURN,
  TabChat,
  TabDatabaseMetadata,
  TabDatabaseSchema,
  TabDatabaseTable,
  TabDataGovernanceDashboard,
  TabStatic,
  TabTurn,
  TabTurnStep,
  TabTypeStatic,
  TabTypeTurnStep,
} from './types'

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Generates a tab ID
 */
export const generateTabId = (): string => nanoid(6)

// =============================================================================
// FACTORY FUNCTIONS
// =============================================================================

/**
 * Options for creating a tab (excluding id and title which are auto-generated)
 */
export type TabCreationOptions = Omit<Tab, 'id' | 'title'>

/**
 * Generic tab creation function that delegates to specific creators based on type
 */
export const createTab = ({
  type,
  title,
  ...options
}: Readonly<{ title?: string | null } & TabCreationOptions>): Tab => {
  const tabShell = { type, ...options } as Partial<Tab>

  // Route to appropriate creator based on tab type
  if (isStaticTab(tabShell)) {
    return createStaticTab(type as TabTypeStatic)
  }

  if (isWorkflowTab(tabShell)) {
    return createWorkflowTab({
      ...tabShell,
      title: title || getTabMetadata(type).title,
    })
  }

  if (isTurnTab(tabShell)) {
    return createTurnTab({
      ...tabShell,
      title: title || getTabMetadata(tabShell.type).title,
    })
  }

  if (isTurnStepTab(tabShell)) {
    return createTurnStepTab({
      ...tabShell,
      title: title || getTabMetadata(tabShell.type).title,
    })
  }

  if (isDatabaseSchemaTab(tabShell)) {
    return createDatabaseSchemaTab({
      ...tabShell,
      title: title || tabShell.databaseName || getTabMetadata(TAB_TYPE_DATABASE_SCHEMA).title,
    })
  }

  if (isDatabaseTableTab(tabShell)) {
    return createDatabaseTableTab({
      ...tabShell,
      title: title || tabShell.schemaName || getTabMetadata(TAB_TYPE_DATABASE_TABLE).title,
    })
  }

  if (isDatabaseMetadataTab(tabShell)) {
    return createDatabaseMetadataTab({
      ...tabShell,
      title: title || tabShell.tableName || getTabMetadata(TAB_TYPE_DATABASE_METADATA).title,
    })
  }

  if (isDataGovernanceDashboardTab(tabShell)) {
    return createDataGovernanceDashboardTab({
      ...tabShell,
      title: title || getTabMetadata(TAB_TYPE_DATA_GOVERNANCE_DASHBOARD).title,
    })
  }

  throw new Error(`Invalid tab type: ${type}`)
}

/**
 * Creates a static tab instance
 */
export const createStaticTab = (type: TabTypeStatic): Tab => ({
  id: generateTabId(),
  title: getTabMetadata(type).title,
  type,
})

/**
 * Creates a workflow tab instance
 */
export const createWorkflowTab = ({
  title,
  workflowId,
}: {
  title?: string | null
  workflowId: string
}): Tab => ({
  id: workflowId,
  title: title || getTabMetadata(TAB_TYPE_CHAT).title,
  type: TAB_TYPE_CHAT,
  workflowId,
})

/**
 * Creates a turn tab instance
 */
export const createTurnTab = ({
  title,
  workflowId,
  turnId,
}: {
  title?: string | null
  workflowId: string
  turnId: string
}): Tab => ({
  id: generateTabId(),
  title: title || getTabMetadata(TAB_TYPE_TURN).title,
  type: TAB_TYPE_TURN,
  workflowId,
  turnId,
})

/**
 * Creates a step tab instance
 */
export const createTurnStepTab = ({
  type,
  title,
  workflowId,
  turnId,
  stepId,
}: {
  type: TabTypeTurnStep
  title?: string | null
  workflowId: string
  turnId: string
  stepId: string
}): Tab => ({
  id: generateTabId(),
  title: title || getTabMetadata(type).title,
  type,
  workflowId,
  turnId,
  stepId,
})

/**
 * Creates a database-related tab instance
 */
export const createDatabaseSchemaTab = ({
  databaseName,
  title,
  assetType,
}: {
  databaseName: string
  title?: string | null
  assetType?: string
}): Tab => ({
  id: generateTabId(),
  title: title || databaseName || getTabMetadata(TAB_TYPE_DATABASE_SCHEMA).title,
  type: TAB_TYPE_DATABASE_SCHEMA,
  databaseName,
  assetType,
})

export const createDatabaseTableTab = ({
  databaseName,
  schemaName,
  title,
  assetType,
}: {
  databaseName: string
  schemaName: string
  title?: string | null
  assetType?: string
}): Tab => ({
  id: generateTabId(),
  title: title || schemaName || getTabMetadata(TAB_TYPE_DATABASE_TABLE).title,
  type: TAB_TYPE_DATABASE_TABLE,
  databaseName,
  schemaName,
  assetType,
})

export const createDatabaseMetadataTab = ({
  databaseName,
  schemaName,
  tableName,
  title,
  assetType,
}: {
  databaseName: string
  schemaName: string
  tableName: string
  title?: string | null
  assetType?: string
}): Tab => ({
  id: generateTabId(),
  title: title || tableName || getTabMetadata(TAB_TYPE_DATABASE_METADATA).title,
  type: TAB_TYPE_DATABASE_METADATA,
  databaseName,
  schemaName,
  tableName,
  assetType,
})

export const createDataGovernanceDashboardTab = ({
  databaseName,
  schemaName,
  tableName,
  title,
  tabView,
  assetType,
  sensitivity,
}: {
  tabView: 'metaData' | 'dataQuality'
  databaseName?: string
  schemaName?: string
  tableName?: string
  title?: string | null
  assetType?: string
  sensitivity?: string
}): Tab => ({
  id: generateTabId(),
  title: title || getTabMetadata(TAB_TYPE_DATA_GOVERNANCE_DASHBOARD).title,
  type: TAB_TYPE_DATA_GOVERNANCE_DASHBOARD,
  databaseName,
  schemaName,
  tableName,
  tabView,
  assetType,
  sensitivity,
})

// =============================================================================
// TYPE GUARDS (imported for factory use)
// =============================================================================

// These are imported from types.ts but needed for the factory functions
const isStaticTab = (tab: Tab | Partial<Tab>): tab is TabStatic =>
  Object.values(TabTypeStatic).includes(tab.type as TabTypeStatic)

const isWorkflowTab = (tab: Tab | Partial<Tab>): tab is TabChat =>
  tab.type === TAB_TYPE_CHAT && 'workflowId' in tab

const isTurnTab = (tab: Tab | Partial<Tab>): tab is TabTurn =>
  tab.type === TAB_TYPE_TURN && 'turnId' in tab && 'workflowId' in tab

const isTurnStepTab = (tab: Tab | Partial<Tab>): tab is TabTurnStep =>
  Object.values(TabTypeTurnStep).includes(tab.type as TabTypeTurnStep) &&
  'stepId' in tab &&
  'turnId' in tab &&
  'workflowId' in tab

const isDatabaseSchemaTab = (tab: Tab | Partial<Tab>): tab is TabDatabaseSchema =>
  tab.type === TAB_TYPE_DATABASE_SCHEMA && 'databaseName' in tab

const isDatabaseTableTab = (tab: Tab | Partial<Tab>): tab is TabDatabaseTable =>
  tab.type === TAB_TYPE_DATABASE_TABLE && 'databaseName' in tab && 'schemaName' in tab

const isDatabaseMetadataTab = (tab: Tab | Partial<Tab>): tab is TabDatabaseMetadata =>
  tab.type === TAB_TYPE_DATABASE_METADATA &&
  'databaseName' in tab &&
  'schemaName' in tab &&
  'tableName' in tab

export const isDataGovernanceDashboardTab = (
  tab: Tab | Partial<Tab>,
): tab is TabDataGovernanceDashboard =>
  tab.type === TAB_TYPE_DATA_GOVERNANCE_DASHBOARD &&
  'databaseName' in tab &&
  'schemaName' in tab &&
  'tableName' in tab &&
  'tabView' in tab
